import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useFetch from "../../global/hooks/useFetch";
import AnimatedNumber from "../../common/components/AnimatedNumber";

const EpisodesTab = ({ seriesId }) => {
  const { t } = useTranslation("assets");

  const { data: episodesData, loading: episodesLoading } = useFetch(
    `/assets/?parent_asset_id=${seriesId}&page_size=100`,
  );

  const { data: analyticsData, loading: analyticsLoading } = useFetch(
    `/analytics/${seriesId}/`,
  );

  const episodes = episodesData?.results || [];
  const breakdown = analyticsData?.series_breakdown;

  if (episodesLoading || analyticsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <Spinner animation="border" />
      </Box>
    );
  }

  return (
    <Box>
      {/* Side-by-side Revenue Breakdown */}
      {breakdown && (
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Series-Level Revenue */}
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography variant="bodyMd" fontWeight={600} sx={{ mb: 1 }}>
                {t("seriesRevenue")}
              </Typography>
              <Typography variant="h3">
                <AnimatedNumber
                  value={breakdown.series_revenue.total}
                  isCurrency
                  section="statsCards"
                />
              </Typography>
            </Box>

            {/* Episode-Level Revenue */}
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography variant="bodyMd" fontWeight={600} sx={{ mb: 1 }}>
                {t("episodeRevenue")}
              </Typography>
              <Typography variant="h3">
                <AnimatedNumber
                  value={breakdown.total_episode_revenue}
                  isCurrency
                  section="statsCards"
                />
              </Typography>
            </Box>

            {/* Combined Total */}
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: "primary.main",
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="bodyMd" fontWeight={600} sx={{ mb: 1 }}>
                {t("combinedTotal")}
              </Typography>
              <Typography variant="h3" color="primary">
                <AnimatedNumber
                  value={breakdown.combined_total}
                  isCurrency
                  section="statsCards"
                />
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Episodes Table */}
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t("episodes")} ({episodes.length})
      </Typography>

      {episodes.length === 0 ? (
        <Typography variant="bodyMd" color="textSecondary">
          {t("noAssets")}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("editModal.titleLabel")}</TableCell>
                <TableCell align="right">{t("metadata.platform")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {episodes.map((ep) => (
                <TableRow key={ep.id} hover>
                  <TableCell>
                    <Typography
                      component={Link}
                      to={`/assets/${ep.id}`}
                      variant="bodyMd"
                      sx={{
                        textDecoration: "none",
                        color: "primary.main",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {ep.title}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {ep.source?.platform || t("metadata.unknown")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Per-Episode Revenue Breakdown */}
      {breakdown?.episode_revenues?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("episodeRevenue")}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("editModal.titleLabel")}</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {breakdown.episode_revenues.map((ep) => (
                  <TableRow key={ep.asset_id} hover>
                    <TableCell>
                      <Typography
                        component={Link}
                        to={`/assets/${ep.asset_id}`}
                        variant="bodyMd"
                        sx={{
                          textDecoration: "none",
                          color: "primary.main",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {ep.title}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <AnimatedNumber
                        value={ep.revenue}
                        isCurrency
                        section="tables"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default EpisodesTab;
